import React, { useEffect, useRef, useState } from "react";
import isEmpty from "lodash/isEmpty";

import { CommonService } from "@/services";
import LocalStorageService from "@/services/StorageService";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { hidePageLoader, showPageLoader } from "@/redux/actions/common.action";
import {
  DEFAULT_PAGE_LOADER_CONFIG,
  EXPRESS_DELIVERY_STATE_ID_KEY
} from "@/constants";
import { selectLoggedInStatus } from "@/util/selectors";
import DeliveryTimelineDropdown from "@/components/deliveryTimelineDropdown";

import { ExpressDeliveryTimelinesProps } from "./types";

const ExpressDeliveryTimelines: React.FC<ExpressDeliveryTimelinesProps> = ({
  deliveryTimelines: initialTimelines,
  scope,
  selectedSizeSource
}) => {
  const [selectedDeliveryTimeline, setSelectedDeliveryTimeline] =
    useState(null);
  const [deliveryTimelines, setDeliveryTimelines] = useState(
    initialTimelines || []
  );
  const dispatch = useAppDispatch();
  const isUserLoggedIn = useAppSelector(selectLoggedInStatus);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const isFirstEffectRef = useRef(true);

  const processTimelines = data => {
    setDeliveryTimelines(data);
    return data;
  };

  const resolveSelectedTimeline = (allTimelines, loggedIn) => {
    let initialTimeline = null;
    if (loggedIn) {
      initialTimeline = allTimelines.find(t => t.isSelected) || allTimelines[0];
      LocalStorageService.remove(EXPRESS_DELIVERY_STATE_ID_KEY);
    } else {
      const guestStateId = LocalStorageService.get(
        EXPRESS_DELIVERY_STATE_ID_KEY
      );
      if (guestStateId !== null && guestStateId !== undefined) {
        initialTimeline =
          allTimelines.find(t => t.stateId === Number(guestStateId)) || null;
      }
      if (!initialTimeline) {
        initialTimeline =
          allTimelines.find(t => t.isSelected) || allTimelines[0];
      }
    }
    setSelectedDeliveryTimeline(initialTimeline);
  };

  useEffect(() => {
    const fetchAndSetInitialTimeline = async () => {
      if (isFirstEffectRef.current && !isEmpty(initialTimelines)) {
        isFirstEffectRef.current = false;
        resolveSelectedTimeline(initialTimelines, isUserLoggedIn);
        return;
      }
      isFirstEffectRef.current = false;

      try {
        dispatch(showPageLoader(DEFAULT_PAGE_LOADER_CONFIG));
        const params = scopeRef.current
          ? { scope: scopeRef.current }
          : undefined;
        const timelinesData = await CommonService.getExpressDeliveryTimeline(
          params
        );

        if (!timelinesData || isEmpty(timelinesData)) return;

        const allTimelines = processTimelines(timelinesData);
        resolveSelectedTimeline(allTimelines, isUserLoggedIn);
      } catch (error) {
        console.error("Issue:", error);
      } finally {
        dispatch(hidePageLoader(DEFAULT_PAGE_LOADER_CONFIG));
      }
    };

    fetchAndSetInitialTimeline();
  }, [isUserLoggedIn]);

  useEffect(() => {
    if (selectedSizeSource == null) return;

    const fetchTimelinesForSize = async () => {
      try {
        dispatch(showPageLoader(DEFAULT_PAGE_LOADER_CONFIG));
        const timelinesData = await CommonService.getExpressDeliveryTimeline({
          scope: selectedSizeSource
        });
        if (timelinesData && !isEmpty(timelinesData)) {
          processTimelines(timelinesData);
          resolveSelectedTimeline(timelinesData, isUserLoggedIn);
        }
      } catch (error) {
        console.error("Issue:", error);
      } finally {
        dispatch(hidePageLoader(DEFAULT_PAGE_LOADER_CONFIG));
      }
    };

    fetchTimelinesForSize();
  }, [selectedSizeSource]);

  const onDeliveryChange = async timeline => {
    setSelectedDeliveryTimeline(timeline);

    if (isUserLoggedIn) {
      try {
        dispatch(showPageLoader(DEFAULT_PAGE_LOADER_CONFIG));
        const newTimelinesData = await CommonService.putExpressDeliveryTimeline(
          { stateId: timeline.stateId },
          scopeRef.current ? { scope: scopeRef.current } : undefined
        );
        if (newTimelinesData) {
          processTimelines(newTimelinesData);
        }
      } catch (error) {
        console.error("Issue:", error);
      } finally {
        dispatch(hidePageLoader(DEFAULT_PAGE_LOADER_CONFIG));
      }
    } else {
      LocalStorageService.set(EXPRESS_DELIVERY_STATE_ID_KEY, timeline.stateId);
    }
  };

  if (isEmpty(deliveryTimelines) || isEmpty(selectedDeliveryTimeline))
    return null;

  return (
    <DeliveryTimelineDropdown
      deliveryTimelines={deliveryTimelines}
      selectedDeliveryTimeline={selectedDeliveryTimeline}
      onDeliveryChange={onDeliveryChange}
    />
  );
};

export default React.memo(ExpressDeliveryTimelines);
